import type {
  TerminalAttachment,
  TerminalClientMessage,
  TerminalEventPayload,
  TerminalServerMessage,
} from '@this+that/models';

import type {
  TerminalConnectionStatus,
  TerminalSendFailure,
  TerminalTransportKind,
} from '../../../enums/terminal';

export type TerminalMessageListener = (message: TerminalServerMessage) => void;
export type TerminalStatusListener = (status: TerminalConnectionStatus) => void;
export type TerminalUnsubscribe = () => void;

export interface TerminalTransport {
  connect: () => void;
  send: (message: TerminalClientMessage) => void | Promise<void>;
  onMessage: (listener: TerminalMessageListener) => TerminalUnsubscribe;
  onStatus: (listener: TerminalStatusListener) => TerminalUnsubscribe;
  close: () => void;
  getKind: () => TerminalTransportKind;
  getDeviceId?: () => string | null;
  uploadAttachment?: (
    file: File,
    onProgress: (fraction: number) => void,
  ) => Promise<TerminalAttachment>;
}

export interface TerminalDevice {
  deviceId: string;
  name: string;
  isConnected: boolean;
  lastSeenAt?: string;
}

export class TerminalSendError extends Error {
  readonly failure: TerminalSendFailure;

  constructor(failure: TerminalSendFailure) {
    super(failure);
    this.failure = failure;
  }
}

export interface TerminalAttachmentUpload {
  attachmentId: string;
  uploadUrl: string;
  uploadFields: Record<string, string>;
  downloadUrl: string;
}

export interface TerminalGateway {
  createAttachmentUpload: (
    deviceId: string,
    file: { name: string; contentType: string; size: number },
  ) => Promise<TerminalAttachmentUpload>;
  listDevices: () => Promise<TerminalDevice[]>;
  createTicket: (deviceId: string) => Promise<string>;
  sendMessage: (
    deviceId: string,
    message: TerminalClientMessage,
  ) => Promise<void>;
  subscribeEvents: (
    listener: (payload: TerminalEventPayload) => void,
  ) => TerminalUnsubscribe;
  isSocketConnected: () => boolean;
  subscribeSocketState: (
    listener: (isConnected: boolean) => void,
  ) => TerminalUnsubscribe;
}
